/**
 * Ad Placements Management - Platform admin page for managing advertising inventory slots
 * Premium dark mode design with placement preview cards
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  LayoutGrid,
  Plus,
  Search,
  Monitor,
  Smartphone,
  Square,
  RectangleHorizontal,
  Maximize2,
  Settings2,
  Eye,
  EyeOff,
  Crown,
  Globe,
  Building2,
  Edit2,
  Check,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Glass panel component
const GlassPanel = ({ children, className = '', ...props }) => (
  <div
    className={`bg-white/[0.03] backdrop-blur-xl border border-white/[0.06] rounded-2xl ${className}`}
    {...props}
  >
    {children}
  </div>
);

// Get icon for placement type
const getPlacementIcon = (placement) => {
  const { width, height, position } = placement;
  const ratio = width / height;
  
  if (position === 'overlay' || placement.placement_key === 'interstitial') return Maximize2;
  if (ratio > 3) return RectangleHorizontal;
  if (ratio === 1) return Square;
  if (width <= 320) return Smartphone;
  return Monitor;
};

// Get page display name
const pageDisplayNames = {
  discover: 'Discovery',
  event: 'Event Detail',
  leaderboard: 'Leaderboards',
  passport: 'Passport',
  scanner: 'Door Scanner',
  app: 'App-wide',
  platform: 'Platform Admin',
};

// Placement Preview Card with visual dimension preview
const PlacementPreviewCard = ({ placement, onToggle, onEdit, isSelected, onSelect }) => {
  const Icon = getPlacementIcon(placement);
  const maxPreviewWidth = 200;
  const maxPreviewHeight = 100;
  
  // Calculate preview dimensions maintaining aspect ratio
  const aspectRatio = placement.width / placement.height;
  let previewWidth, previewHeight;
  
  if (aspectRatio > maxPreviewWidth / maxPreviewHeight) {
    previewWidth = maxPreviewWidth;
    previewHeight = maxPreviewWidth / aspectRatio;
  } else {
    previewHeight = maxPreviewHeight;
    previewWidth = maxPreviewHeight * aspectRatio;
  }

  return (
    <GlassPanel 
      className={`
        p-4 transition-all duration-300 cursor-pointer
        ${isSelected ? 'border-electric-ice-blue/50 bg-electric-ice-blue/5' : 'hover:border-white/[0.12]'}
        ${!placement.is_active ? 'opacity-60' : ''}
      `}
      onClick={() => onSelect?.(placement)}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <div className={`
            w-8 h-8 rounded-lg flex items-center justify-center
            ${placement.is_premium ? 'bg-soft-amber/20' : 'bg-white/[0.05]'}
          `}>
            <Icon className={`w-4 h-4 ${placement.is_premium ? 'text-soft-amber' : 'text-soft-silver'}`} />
          </div>
          <div>
            <h3 className="text-sm font-medium text-cloud-white">{placement.placement_name}</h3>
            <p className="text-xs text-muted-text font-mono">{placement.placement_key}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {placement.is_premium && (
            <Crown className="w-4 h-4 text-soft-amber" />
          )}
          <Switch
            checked={placement.is_active}
            onCheckedChange={(checked) => {
              onToggle(placement.id, checked);
            }}
            onClick={(e) => e.stopPropagation()}
            className="data-[state=checked]:bg-cool-mint"
          />
        </div>
      </div>

      {/* Dimension Preview */}
      <div className="flex justify-center items-center p-4 mb-3 bg-obsidian/50 rounded-lg border border-white/[0.04]">
        <div 
          className={`
            border-2 border-dashed rounded flex items-center justify-center
            ${placement.is_active ? 'border-electric-ice-blue/50 bg-electric-ice-blue/5' : 'border-white/[0.1] bg-white/[0.02]'}
          `}
          style={{ 
            width: `${previewWidth}px`, 
            height: `${previewHeight}px`,
            minHeight: '30px'
          }}
        >
          <span className="text-xs font-mono text-muted-text">
            {placement.width}×{placement.height}
          </span>
        </div>
      </div>

      {/* Details */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-text">Page</span>
          <Badge variant="outline" className="text-xs border-white/[0.1]">
            {pageDisplayNames[placement.page] || placement.page}
          </Badge>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-text">Position</span>
          <span className="text-soft-silver capitalize">{placement.position}</span>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-text">Max Ads</span>
          <span className="text-soft-silver">{placement.max_ads}</span>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-text">Scope</span>
          <div className="flex items-center gap-1">
            {placement.scope === 'platform' ? (
              <Globe className="w-3 h-3 text-electric-ice-blue" />
            ) : (
              <Building2 className="w-3 h-3 text-soft-amber" />
            )}
            <span className="text-soft-silver capitalize">{placement.scope}</span>
          </div>
        </div>
      </div>

      {/* Description */}
      {placement.description && (
        <p className="mt-3 pt-3 border-t border-white/[0.06] text-xs text-muted-text line-clamp-2">
          {placement.description}
        </p>
      )}

      {/* Edit Button */}
      <div className="mt-3 pt-3 border-t border-white/[0.06]">
        <Button 
          variant="ghost" 
          size="sm" 
          className="w-full h-8 text-xs"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(placement);
          }}
        >
          <Edit2 className="w-3 h-3 mr-2" /> Edit Placement
        </Button>
      </div>
    </GlassPanel>
  );
};

// Placement Catalog Card (for predefined slots)
const CatalogCard = ({ placementKey, definition, isSeeded, onSeed }) => {
  const Icon = definition.width === definition.height ? Square : 
               definition.width / definition.height > 3 ? RectangleHorizontal : Monitor;

  return (
    <div className={`
      p-4 rounded-xl border transition-all
      ${isSeeded 
        ? 'bg-cool-mint/5 border-cool-mint/30' 
        : 'bg-white/[0.02] border-white/[0.06] hover:border-white/[0.12]'
      }
    `}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-soft-silver" />
          <span className="text-sm font-medium text-cloud-white">{definition.placement_name}</span>
        </div>
        {isSeeded ? (
          <Badge className="bg-cool-mint/20 text-cool-mint border-cool-mint/30">
            <Check className="w-3 h-3 mr-1" /> Active
          </Badge>
        ) : (
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onSeed?.(placementKey)}>
            Seed
          </Button>
        )}
      </div>
      <div className="flex items-center gap-3 text-xs text-muted-text">
        <span className="font-mono">{definition.width}×{definition.height}</span>
        <span>•</span>
        <span>{pageDisplayNames[definition.page] || definition.page}</span>
        {definition.is_premium && (
          <>
            <span>•</span>
            <Crown className="w-3 h-3 text-soft-amber" />
          </>
        )}
      </div>
    </div>
  );
};

// Edit Placement Form
const PlacementForm = ({ placement, onSave, onClose }) => {
  const [form, setForm] = useState({
    placement_name: placement?.placement_name || '',
    description: placement?.description || '',
    width: placement?.width || 300,
    height: placement?.height || 100,
    page: placement?.page || 'app',
    position: placement?.position || 'inline',
    max_ads: placement?.max_ads || 1,
    priority: placement?.priority || 50,
    is_active: placement?.is_active ?? true,
    is_premium: placement?.is_premium || false,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5 py-4">
      <div>
        <Label>Placement Name</Label>
        <Input
          value={form.placement_name}
          onChange={(e) => setForm({ ...form, placement_name: e.target.value })}
          className="mt-1.5 bg-white/[0.03] border-white/[0.1]"
        />
      </div>

      <div>
        <Label>Description</Label>
        <Textarea
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          className="mt-1.5 bg-white/[0.03] border-white/[0.1]"
          rows={2}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Width (px)</Label>
          <Input
            type="number"
            value={form.width}
            onChange={(e) => setForm({ ...form, width: parseInt(e.target.value) || 0 })}
            className="mt-1.5 bg-white/[0.03] border-white/[0.1]"
          />
        </div>
        <div>
          <Label>Height (px)</Label>
          <Input
            type="number"
            value={form.height}
            onChange={(e) => setForm({ ...form, height: parseInt(e.target.value) || 0 })}
            className="mt-1.5 bg-white/[0.03] border-white/[0.1]"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Page</Label>
          <Select value={form.page} onValueChange={(v) => setForm({ ...form, page: v })}>
            <SelectTrigger className="mt-1.5 bg-white/[0.03] border-white/[0.1]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="discover">Discovery</SelectItem>
              <SelectItem value="event">Event Detail</SelectItem>
              <SelectItem value="leaderboard">Leaderboards</SelectItem>
              <SelectItem value="passport">Passport</SelectItem>
              <SelectItem value="scanner">Door Scanner</SelectItem>
              <SelectItem value="app">App-wide</SelectItem>
              <SelectItem value="platform">Platform Admin</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Position</Label>
          <Select value={form.position} onValueChange={(v) => setForm({ ...form, position: v })}>
            <SelectTrigger className="mt-1.5 bg-white/[0.03] border-white/[0.1]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="header">Header</SelectItem>
              <SelectItem value="inline">Inline</SelectItem>
              <SelectItem value="sidebar">Sidebar</SelectItem>
              <SelectItem value="footer">Footer</SelectItem>
              <SelectItem value="overlay">Overlay</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Max Ads</Label>
          <Input
            type="number"
            min="1"
            max="10"
            value={form.max_ads}
            onChange={(e) => setForm({ ...form, max_ads: parseInt(e.target.value) || 1 })}
            className="mt-1.5 bg-white/[0.03] border-white/[0.1]"
          />
        </div>
        <div>
          <Label>Priority (1-100)</Label>
          <Input
            type="number"
            min="1"
            max="100"
            value={form.priority}
            onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value) || 50 })}
            className="mt-1.5 bg-white/[0.03] border-white/[0.1]"
          />
        </div>
      </div>

      <div className="flex items-center gap-6 pt-2">
        <div className="flex items-center gap-2">
          <Switch
            checked={form.is_active}
            onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
            className="data-[state=checked]:bg-cool-mint"
          />
          <Label>Active</Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            checked={form.is_premium}
            onCheckedChange={(checked) => setForm({ ...form, is_premium: checked })}
            className="data-[state=checked]:bg-soft-amber"
          />
          <Label>Premium</Label>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-white/[0.06]">
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </form>
  );
};

export const AdPlacements = () => {
  const { session, profile } = useAuth();
  const [placements, setPlacements] = useState([]);
  const [catalog, setCatalog] = useState({});
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('inventory');
  const [searchQuery, setSearchQuery] = useState('');
  const [scopeFilter, setScopeFilter] = useState('all');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingPlacement, setEditingPlacement] = useState(null);
  const [selectedPlacement, setSelectedPlacement] = useState(null);

  const isPlatformAdmin = ['platform_owner', 'platform_admin'].includes(profile?.platform_role);

  // Fetch data
  const fetchData = useCallback(async () => {
    if (!session?.access_token) {
      setLoading(false);
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${session.access_token}` };
      
      const [placementsRes, catalogRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/placements?limit=100`, { headers }),
        fetch(`${API_URL}/api/placements/catalog`, { headers }),
        fetch(`${API_URL}/api/placements/stats`, { headers }),
      ]);

      if (placementsRes.ok) {
        const data = await placementsRes.json();
        setPlacements(data.placements || []);
      }

      if (catalogRes.ok) {
        const data = await catalogRes.json();
        setCatalog(data.catalog || {});
      }

      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Failed to fetch placements:', err);
    } finally {
      setLoading(false);
    }
  }, [session?.access_token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Toggle placement status
  const handleToggle = async (placementId, isActive) => {
    try {
      const res = await fetch(`${API_URL}/api/placements/${placementId}/toggle?is_active=${isActive}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (res.ok) {
        toast.success(isActive ? 'Placement activated' : 'Placement deactivated');
        fetchData();
      }
    } catch (err) {
      console.error('Failed to toggle placement:', err);
      toast.error('Failed to update placement');
    }
  };

  // Update placement
  const handleSavePlacement = async (formData) => {
    if (!editingPlacement) return;

    try {
      const res = await fetch(`${API_URL}/api/placements/${editingPlacement.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (res.ok) {
        toast.success('Placement updated');
        setIsFormOpen(false);
        setEditingPlacement(null);
        fetchData();
      } else {
        toast.error('Failed to update placement');
      }
    } catch (err) {
      console.error('Failed to save placement:', err);
      toast.error('Failed to save placement');
    }
  };

  // Seed predefined placements
  const handleSeedAll = async () => {
    try {
      const res = await fetch(`${API_URL}/api/placements/seed`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Seeded ${data.seeded} placements`);
        fetchData();
      }
    } catch (err) {
      console.error('Failed to seed placements:', err);
      toast.error('Failed to seed placements');
    }
  };

  // Filter placements
  const filteredPlacements = placements.filter((p) => {
    const matchesSearch = 
      p.placement_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.placement_key.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesScope = scopeFilter === 'all' || p.scope === scopeFilter;
    return matchesSearch && matchesScope;
  });

  // Check which catalog items are seeded
  const seededKeys = new Set(placements.map(p => p.placement_key));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-electric-ice-blue border-t-transparent rounded-full animate-spin" />
          <p className="text-soft-silver text-sm">Loading placements...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="ad-placements-page" className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-cloud-white flex items-center gap-3">
            <LayoutGrid className="w-8 h-8 text-electric-ice-blue" />
            Ad Placements
          </h1>
          <p className="text-soft-silver mt-1">Manage advertising inventory slots</p>
        </div>

        {isPlatformAdmin && placements.length === 0 && (
          <Button onClick={handleSeedAll}>
            <Plus className="w-4 h-4 mr-2" /> Seed V1 Placements
          </Button>
        )}
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <GlassPanel className="p-4">
            <p className="text-muted-text text-xs">Total Slots</p>
            <p className="text-xl font-mono font-bold text-cloud-white mt-1">{stats.total}</p>
          </GlassPanel>
          <GlassPanel className="p-4">
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-cool-mint" />
              <p className="text-muted-text text-xs">Active</p>
            </div>
            <p className="text-xl font-mono font-bold text-cool-mint mt-1">{stats.active}</p>
          </GlassPanel>
          <GlassPanel className="p-4">
            <div className="flex items-center gap-2">
              <EyeOff className="w-4 h-4 text-muted-text" />
              <p className="text-muted-text text-xs">Inactive</p>
            </div>
            <p className="text-xl font-mono font-bold text-soft-silver mt-1">{stats.inactive}</p>
          </GlassPanel>
          <GlassPanel className="p-4">
            <div className="flex items-center gap-2">
              <Crown className="w-4 h-4 text-soft-amber" />
              <p className="text-muted-text text-xs">Premium</p>
            </div>
            <p className="text-xl font-mono font-bold text-soft-amber mt-1">{stats.premium}</p>
          </GlassPanel>
          <GlassPanel className="p-4">
            <p className="text-muted-text text-xs">Pages</p>
            <p className="text-xl font-mono font-bold text-electric-ice-blue mt-1">{stats.pages?.length || 0}</p>
          </GlassPanel>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white/[0.03] border border-white/[0.06] p-1 rounded-xl">
          <TabsTrigger 
            value="inventory"
            className="data-[state=active]:bg-electric-ice-blue data-[state=active]:text-obsidian rounded-lg"
          >
            <LayoutGrid className="w-4 h-4 mr-2" />
            Inventory
          </TabsTrigger>
          <TabsTrigger 
            value="catalog"
            className="data-[state=active]:bg-electric-ice-blue data-[state=active]:text-obsidian rounded-lg"
          >
            <Settings2 className="w-4 h-4 mr-2" />
            Catalog
          </TabsTrigger>
        </TabsList>

        {/* Inventory Tab */}
        <TabsContent value="inventory" className="mt-6">
          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-text" />
              <Input
                placeholder="Search placements..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white/[0.03] border-white/[0.1]"
              />
            </div>
            <Select value={scopeFilter} onValueChange={setScopeFilter}>
              <SelectTrigger className="w-[160px] bg-white/[0.03] border-white/[0.1]">
                <SelectValue placeholder="All Scopes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Scopes</SelectItem>
                <SelectItem value="platform">Platform</SelectItem>
                <SelectItem value="organization">Organization</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Placements Grid */}
          {filteredPlacements.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredPlacements.map((placement) => (
                <PlacementPreviewCard
                  key={placement.id}
                  placement={placement}
                  onToggle={handleToggle}
                  onEdit={(p) => { setEditingPlacement(p); setIsFormOpen(true); }}
                  isSelected={selectedPlacement?.id === placement.id}
                  onSelect={setSelectedPlacement}
                />
              ))}
            </div>
          ) : (
            <GlassPanel className="p-12 text-center">
              <LayoutGrid className="w-12 h-12 text-muted-text mx-auto mb-4" />
              <h3 className="text-lg font-medium text-cloud-white mb-2">No Placements</h3>
              <p className="text-muted-text mb-4">
                {searchQuery ? 'No placements match your search.' : 'Seed the V1 placements to get started.'}
              </p>
              {isPlatformAdmin && !searchQuery && (
                <Button onClick={handleSeedAll}>
                  <Plus className="w-4 h-4 mr-2" /> Seed V1 Placements
                </Button>
              )}
            </GlassPanel>
          )}
        </TabsContent>

        {/* Catalog Tab */}
        <TabsContent value="catalog" className="mt-6">
          <div className="mb-4">
            <h3 className="text-lg font-medium text-cloud-white mb-2">Predefined V1 Placements</h3>
            <p className="text-sm text-muted-text">
              Standard ad slot definitions. Click "Seed V1 Placements" to activate all.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(catalog).map(([key, definition]) => (
              <CatalogCard
                key={key}
                placementKey={key}
                definition={definition}
                isSeeded={seededKeys.has(key)}
              />
            ))}
          </div>

          {Object.keys(catalog).length > 0 && !seededKeys.size && isPlatformAdmin && (
            <div className="mt-6 text-center">
              <Button onClick={handleSeedAll} size="lg">
                <Plus className="w-4 h-4 mr-2" /> Seed All V1 Placements
              </Button>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Edit Sheet */}
      <Sheet open={isFormOpen} onOpenChange={setIsFormOpen}>
        <SheetContent className="bg-graphite border-white/[0.06] w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-cloud-white">
              Edit Placement
            </SheetTitle>
            <SheetDescription className="text-muted-text">
              Update placement settings and dimensions
            </SheetDescription>
          </SheetHeader>
          {editingPlacement && (
            <PlacementForm
              placement={editingPlacement}
              onSave={handleSavePlacement}
              onClose={() => { setIsFormOpen(false); setEditingPlacement(null); }}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default AdPlacements;
